import React, { useState, useMemo, useEffect } from 'react';
import { Star as StarIcon } from 'lucide-react';
import { useProducts } from '../hooks/useProducts';
import { productImgProps } from '../../../shared/lib/images';

/* ── Design tokens ───────────────────────────────────────────── */
const INK      = 'var(--bg-ink)';
const PANEL    = 'var(--bg-panel)';
const HAIRLINE = 'var(--hairline)';
const HAIRLINE_STRONG = 'var(--hairline-strong)';
const CREAM    = 'var(--color-cream)';
const MUTED    = 'var(--color-muted)';
const FAINT    = 'var(--color-faint)';
const ACCENT   = 'var(--accent)';
const RED      = 'var(--danger)';
const WHITE    = '#FFFFFF';

const fDisplay = "'Space Grotesk', system-ui, sans-serif";
const fMono    = "'Space Mono', ui-monospace, Menlo, monospace";
const fBody    = "'Inter', system-ui, sans-serif";

/* ── Breakpoint hook ─────────────────────────────────────────── */
const useBreakpoint = () => {
  const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { isMobile: w < 720, isTablet: w < 960 };
};

/* ── Micro label ─────────────────────────────────────────────── */
const Micro = ({ children, color = FAINT, style = {} }) => (
  <span style={{
    fontFamily: fMono, fontSize: 10, fontWeight: 400,
    letterSpacing: '0.22em', textTransform: 'uppercase',
    color, whiteSpace: 'nowrap', ...style,
  }}>
    {children}
  </span>
);

/* ── Tag inline (relleno acento / rojo) ──────────────────────── */
const Tag = ({ label, variant = 'cool' }) => {
  const styles = {
    flash:   { bg: RED,                            text: WHITE,  border: 'none' },
    cool:    { bg: 'var(--accent-tint)',       text: ACCENT, border: '1px solid var(--accent-tint)' },
    neutral: { bg: 'rgba(245,245,240,0.05)',       text: MUTED,  border: `1px solid ${HAIRLINE_STRONG}` },
  };
  const s = styles[variant] || styles.cool;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '4px 9px',
      background: s.bg, color: s.text, border: s.border,
      fontFamily: fBody, fontSize: 10, fontWeight: 600,
      letterSpacing: '0.06em', textTransform: 'uppercase',
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
};

/* ── Filter row ──────────────────────────────────────────────── */
const FilterRow = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      width: '100%', padding: '14px 0', background: 'none', border: 'none',
      borderBottom: `1px solid ${active ? HAIRLINE_STRONG : HAIRLINE}`,
      cursor: 'pointer', textAlign: 'left',
      transition: 'border-color 0.25s',
    }}
    onMouseEnter={e => { if (!active) e.currentTarget.style.borderBottomColor = HAIRLINE_STRONG; }}
    onMouseLeave={e => { if (!active) e.currentTarget.style.borderBottomColor = HAIRLINE; }}
  >
    <span style={{
      fontFamily: fBody, fontSize: 13, letterSpacing: '-0.005em',
      fontWeight: active ? 500 : 400,
      color: active ? CREAM : MUTED,
      transition: 'color 0.25s',
    }}>
      {label}
    </span>
    <span style={{
      fontFamily: fMono, fontSize: 10, letterSpacing: '0.06em',
      color: active ? ACCENT : FAINT,
      transition: 'color 0.25s',
    }}>
      {String(count).padStart(2, '0')}
    </span>
  </button>
);

/* ── Filter chip (mobile horizontal) ─────────────────────────── */
const FilterChip = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '8px 14px',
      background: active ? ACCENT : 'transparent',
      color: active ? INK : MUTED,
      border: `1px solid ${active ? ACCENT : HAIRLINE_STRONG}`,
      cursor: 'pointer',
      fontFamily: fBody, fontSize: 12, fontWeight: 500,
      letterSpacing: '-0.005em',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s',
    }}
  >
    {label}
    <span style={{
      fontFamily: fMono, fontSize: 10,
      color: active ? INK : FAINT,
      opacity: active ? 0.7 : 1,
    }}>
      {String(count).padStart(2, '0')}
    </span>
  </button>
);

/* ── helpers ─────────────────────────────────────────────────── */
// Clasificación: 4 grupos → categorías (hojas). `Product.category` guarda la hoja.
// "Robots y tanques" es grupo y hoja a la vez. Los slugs viejos (lego-build…) siguen funcionando
// hasta que se migren los datos.
const TAXONOMY = [
  { group: 'Autos', items: ['Supercars y clásicos', 'Carreras y todoterreno'] },
  { group: 'Maquinaria', items: ['Excavadoras y bulldozers', 'Grúas', 'Camiones y volquetes'] },
  { group: 'Robots y tanques', items: [] },
  { group: 'Trenes y locomotoras', items: [] },
  { group: 'Star Wars', items: ['Sets y figuras', 'Cascos coleccionables', 'Llaveros', 'Pósters'] },
];
const LEGACY_CATEGORY = {
  'lego-build': 'Sets y figuras',
  'lego-helmet': 'Cascos coleccionables',
  'lego-keyring': 'Llaveros',
  posters: 'Pósters',
};

const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

const LEAF_BY_NORM = {};
const LEAF_TO_GROUP = {};
for (const { group, items } of TAXONOMY) {
  LEAF_BY_NORM[norm(group)] = group;
  LEAF_TO_GROUP[group] = group;
  for (const leaf of items) {
    LEAF_BY_NORM[norm(leaf)] = leaf;
    LEAF_TO_GROUP[leaf] = group;
  }
}

const getLeaf = (p) => {
  for (const v of [p?.category, p?.type, p?.purpose]) {
    const k = norm(v);
    if (LEAF_BY_NORM[k]) return LEAF_BY_NORM[k];
    if (LEGACY_CATEGORY[k]) return LEGACY_CATEGORY[k];
  }
  return '';
};
const getGroup = (p) => LEAF_TO_GROUP[getLeaf(p)] || '';
// `sel` puede ser un grupo (todas sus hojas) o una hoja.
const matchesPurpose = (p, sel) => !sel || getLeaf(p) === sel || getGroup(p) === sel;
const inGroup = (sel, group) => !!sel && (sel === group || LEAF_TO_GROUP[sel] === group);
const brandLabel = (b) => (norm(b) === 'displate' ? 'Displate' : b);

// "Oferta Relámpago" = etiqueta 'Price/Quality' del admin (se acepta el legacy 'true').
const isFlashOffer = (p) =>
  p?.approved === 'Price/Quality' || p?.approved === 'true' || p?.approved === true;

/* ── Rating (estrellas) ──────────────────────────────────────── */
const STAR = '#FFB547';

const toFive = (score) => {
  const s = parseFloat(score);
  if (!Number.isFinite(s)) return null;
  if (s <= 5) return s;
  if (s <= 10) return s / 2;
  return s / 20;
};

const Stars = ({ score, size = 12 }) => {
  const value = toFive(score);
  if (value == null) return null;
  const clamped = Math.max(0, Math.min(5, value));
  return (
    <span style={{ display: 'inline-flex', gap: 1, alignItems: 'center' }} aria-label={`${clamped.toFixed(1)} de 5`}>
      {Array.from({ length: 5 }).map((_, i) => {
        const fill = Math.max(0, Math.min(1, clamped - i));
        return (
          <span key={i} style={{ position: 'relative', display: 'inline-block', width: size, height: size, lineHeight: 0 }}>
            <StarIcon size={size} style={{ fill: 'rgba(245,245,240,0.18)', color: 'rgba(245,245,240,0.18)' }} />
            {fill > 0 && (
              <span style={{ position: 'absolute', inset: 0, overflow: 'hidden', width: `${fill * 100}%` }}>
                <StarIcon size={size} style={{ fill: STAR, color: STAR }} />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
};

/* ── Filtro controlado/no-controlado (componente compartido) ── */
// Si el padre pasa el setter → modo controlado (estado vive en el padre).
// Si no → estado local interno (landing usa el catálogo sin pasar setters).
const useFilter = (externalValue, externalSetter, initialInternal) => {
  const [internal, setInternal] = useState(initialInternal);
  const isControlled = typeof externalSetter === 'function';
  return [
    isControlled ? externalValue : internal,
    (v) => { if (isControlled) externalSetter(v); else setInternal(v); },
  ];
};

/* ── Sidebar (desktop) ───────────────────────────────────────── */
const Sidebar = ({
  counts, selectedQuadrant, onSelectQuadrant,
  purposeFilter, setPurposeFilter,
  brands, brand, setBrand,
  factions, faction, setFaction,
  eras, era, setEra,
  collectibleOnly, setCollectibleOnly,
}) => {
  const hasFilters = selectedQuadrant || purposeFilter || brand || faction || era || collectibleOnly;
  const starWars = inGroup(purposeFilter, 'Star Wars');

  const clearAll = () => {
    onSelectQuadrant(null);
    setPurposeFilter(null);
    setBrand(null);
    setFaction(null);
    setEra(null);
    setCollectibleOnly(false);
  };
  const togglePurpose = (v) => setPurposeFilter(purposeFilter === v ? null : v);

  return (
    <div style={{ position: 'sticky', top: 80 }}>
      <Micro style={{ display: 'block', marginBottom: 18 }}>Valor</Micro>

      <FilterRow
        label="Oferta Relámpago" count={counts.legendary}
        active={selectedQuadrant === 'legendary'}
        onClick={() => onSelectQuadrant(selectedQuadrant === 'legendary' ? null : 'legendary')}
      />
      <FilterRow
        label="Coleccionables" count={counts.collectible}
        active={!!collectibleOnly}
        onClick={() => setCollectibleOnly(!collectibleOnly)}
      />

      {brands.length > 0 && (
        <>
          <Micro style={{ display: 'block', marginTop: 36, marginBottom: 18 }}>Marca</Micro>
          {brands.map(([value, count]) => (
            <FilterRow
              key={value}
              label={brandLabel(value)} count={count}
              active={brand === value}
              onClick={() => setBrand(brand === value ? null : value)}
            />
          ))}
        </>
      )}

      <Micro style={{ display: 'block', marginTop: 36, marginBottom: 18 }}>Categoría</Micro>

      {TAXONOMY.map(({ group, items }) => {
        if (!counts.group[group]) return null;
        const open = inGroup(purposeFilter, group);
        return (
          <React.Fragment key={group}>
            <FilterRow
              label={group} count={counts.group[group]}
              active={open}
              onClick={() => togglePurpose(group)}
            />
            {open && items.map((leaf) => (
              counts.leaf[leaf] > 0 && (
                <div key={leaf} style={{ paddingLeft: 16 }}>
                  <FilterRow
                    label={leaf} count={counts.leaf[leaf]}
                    active={purposeFilter === leaf}
                    onClick={() => togglePurpose(leaf)}
                  />
                </div>
              )
            ))}
          </React.Fragment>
        );
      })}

      {starWars && factions.length > 0 && (
        <>
          <Micro style={{ display: 'block', marginTop: 36, marginBottom: 18 }}>Facción</Micro>
          {factions.map(([value, count]) => (
            <FilterRow
              key={value}
              label={value} count={count}
              active={faction === value}
              onClick={() => setFaction(faction === value ? null : value)}
            />
          ))}
        </>
      )}

      {eras.length > 0 && (
        <>
          <Micro style={{ display: 'block', marginTop: 36, marginBottom: 18 }}>Era</Micro>
          {eras.map(([value, count]) => (
            <FilterRow
              key={value}
              label={value} count={count}
              active={era === value}
              onClick={() => setEra(era === value ? null : value)}
            />
          ))}
        </>
      )}

      {hasFilters && (
        <button
          onClick={clearAll}
          style={{
            marginTop: 28, fontFamily: fMono, fontSize: 10,
            letterSpacing: '0.16em', textTransform: 'uppercase',
            color: MUTED, background: 'none', border: 'none',
            cursor: 'pointer', padding: 0,
            transition: 'color 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = CREAM}
          onMouseLeave={e => e.currentTarget.style.color = MUTED}
        >
          ← Limpiar
        </button>
      )}
    </div>
  );
};

/* ── Filter bar (mobile horizontal) ──────────────────────────── */
const FilterBar = ({
  counts, selectedQuadrant, onSelectQuadrant,
  purposeFilter, setPurposeFilter,
  brands, brand, setBrand,
  factions, faction, setFaction,
  eras, era, setEra,
  collectibleOnly, setCollectibleOnly,
}) => {
  const starWars = inGroup(purposeFilter, 'Star Wars');
  const togglePurpose = (v) => setPurposeFilter(purposeFilter === v ? null : v);
  return (
    <div style={{
      display: 'flex', gap: 8, overflowX: 'auto',
      paddingBottom: 12, marginBottom: 12,
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
    }}>
      <FilterChip label="Oferta" count={counts.legendary} active={selectedQuadrant === 'legendary'} onClick={() => onSelectQuadrant(selectedQuadrant === 'legendary' ? null : 'legendary')} />
      <FilterChip label="Colección" count={counts.collectible} active={!!collectibleOnly} onClick={() => setCollectibleOnly(!collectibleOnly)} />
      {brands.map(([value, count]) => (
        <FilterChip key={value} label={brandLabel(value)} count={count} active={brand === value} onClick={() => setBrand(brand === value ? null : value)} />
      ))}
      {TAXONOMY.map(({ group, items }) => (
        counts.group[group] > 0 && (
          <React.Fragment key={group}>
            <FilterChip label={group} count={counts.group[group]} active={inGroup(purposeFilter, group)} onClick={() => togglePurpose(group)} />
            {inGroup(purposeFilter, group) && items.map((leaf) => (
              counts.leaf[leaf] > 0 && (
                <FilterChip key={leaf} label={leaf} count={counts.leaf[leaf]} active={purposeFilter === leaf} onClick={() => togglePurpose(leaf)} />
              )
            ))}
          </React.Fragment>
        )
      ))}
      {starWars && factions.map(([value, count]) => (
        <FilterChip key={value} label={value} count={count} active={faction === value} onClick={() => setFaction(faction === value ? null : value)} />
      ))}
      {eras.map(([value, count]) => (
        <FilterChip key={value} label={value} count={count} active={era === value} onClick={() => setEra(era === value ? null : value)} />
      ))}
    </div>
  );
};

/* ── Product Card (limpia, sin estrellas, imagen blanca) ─────── */
const ProductCard = ({ product, onViewDetail }) => {
  const [hovered, setHovered] = useState(false);
  const categoryLabel = getLeaf(product) || undefined;
  const tag = isFlashOffer(product)
    ? { label: 'Oferta Relámpago', variant: 'flash' }
    : product.is_collectible
      ? { label: 'Coleccionable', variant: 'cool' }
      : categoryLabel
        ? { label: categoryLabel, variant: 'neutral' }
        : null;
  const price = parseFloat(product.price_offer ?? product.price_unit ?? product.price ?? 0);
  const originalPrice = product.price_offer ? parseFloat(product.price ?? product.price_unit ?? 0) : null;
  const discount = originalPrice && originalPrice > price ? Math.round((1 - price / originalPrice) * 100) : 0;
  const stock = parseInt(product.stock ?? 0, 10);
  const stockLabel = stock <= 0
    ? { text: 'Por encargo', color: '#FFB547' }  // activo con stock 0: se vende por encargo
    : stock <= 2
      ? { text: 'Queda poco stock', color: '#FFB547' }
      : null;
  const specs = [product.faction, product.era, product.character_related, product.description].filter(Boolean).join(' · ');

  return (
    <article
      onClick={() => onViewDetail(product)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: PANEL,
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column',
        border: `1px solid ${hovered ? HAIRLINE_STRONG : HAIRLINE}`,
        transition: 'border-color 0.3s ease, transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered ? '0 18px 40px -12px rgba(0,0,0,0.6)' : '0 0 0 rgba(0,0,0,0)',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Image — fondo blanco como Amazon */}
      <div style={{
        position: 'relative',
        background: WHITE,
        aspectRatio: '4/3',
        overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <img
          {...productImgProps(
            product.main_image ?? product.image ?? product.images?.[0]?.image ?? '',
            '(max-width: 640px) 46vw, (max-width: 1024px) 31vw, 300px',
            [400, 800],
          )}
          loading="lazy"
          alt={product.name}
          style={{
            maxWidth: '85%', maxHeight: '85%',
            width: 'auto', height: 'auto',
            objectFit: 'contain',
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 0.5s ease',
          }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      </div>

      {/* Info area — todo alineado verticalmente */}
      <div style={{
        padding: '16px 16px 18px',
        flex: 1,
        display: 'flex', flexDirection: 'column', gap: 0,
      }}>
        {/* Brand */}
        <div style={{ minHeight: 14, marginBottom: 8 }}>
          {product.brand && (
            <Micro style={{ letterSpacing: '0.18em', opacity: 0.75 }}>{product.brand}</Micro>
          )}
        </div>

        {/* Title — altura fija 2 líneas */}
        <h3 style={{
          fontFamily: fBody, fontSize: 14, fontWeight: 500,
          color: CREAM, lineHeight: 1.4, letterSpacing: '-0.005em',
          margin: '0 0 12px',
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
          height: 39,
        }}>
          {product.name}
        </h3>

        {/* Rating — slot fijo para alinear cards (prueba social) */}
        <div style={{ minHeight: 18, marginBottom: 10 }}>
          {product.score != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Stars score={product.score} size={12} />
              <span style={{ fontFamily: fMono, fontSize: 10, color: MUTED, letterSpacing: '0.04em' }}>
                {toFive(product.score)?.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        {/* Tag — altura fija (incluso si no hay tag, reserva el espacio para alineación) */}
        <div style={{ minHeight: 22, marginBottom: 12 }}>
          {tag && <Tag label={tag.label} variant={tag.variant} />}
        </div>

        {/* Discount + Price — fila bien alineada */}
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 10,
          marginBottom: 4, flexWrap: 'wrap',
        }}>
          {discount > 0 && (
            <span style={{
              fontFamily: fBody, fontSize: 15, fontWeight: 600,
              color: RED, letterSpacing: '-0.01em', lineHeight: 1,
            }}>
              -{discount}%
            </span>
          )}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{
              fontFamily: fBody, fontSize: 12, color: MUTED,
              fontWeight: 500, lineHeight: 1,
            }}>
              S/
            </span>
            <span style={{
              fontFamily: fDisplay, fontSize: 24, fontWeight: 500,
              color: CREAM, letterSpacing: '-0.025em', lineHeight: 1,
            }}>
              {price.toLocaleString('es-PE', { minimumFractionDigits: 0 })}
            </span>
          </div>
        </div>

        {/* PVPR — altura fija */}
        <div style={{ minHeight: 16, marginBottom: 12 }}>
          {originalPrice && (
            <div style={{
              fontFamily: fBody, fontSize: 11, color: FAINT, lineHeight: 1.4,
            }}>
              PVPR: <span style={{ textDecoration: 'line-through' }}>
                S/{originalPrice.toLocaleString('es-PE')}
              </span>
            </div>
          )}
        </div>

        {/* Stock / escasez — altura fija para alinear cards */}
        <div style={{ minHeight: 14, marginBottom: 8 }}>
          {stockLabel && (
            <span style={{
              fontFamily: fMono, fontSize: 9, fontWeight: 500,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: stockLabel.color,
            }}>
              {stockLabel.text}
            </span>
          )}
        </div>

        {/* Specs */}
        {specs && (
          <p style={{
            fontFamily: fBody, fontSize: 12, color: MUTED,
            lineHeight: 1.5, margin: '0 0 6px',
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {specs}
          </p>
        )}

        {/* Delivery */}
        <p style={{
          fontFamily: fBody, fontSize: 11, color: MUTED,
          lineHeight: 1.5, margin: '0 0 14px',
        }}>
          Entrega a todo el <span style={{ color: CREAM, fontWeight: 600 }}>Perú</span>
        </p>

        {/* CTA "Ver detalle" — siempre visible, hover acento */}
        <div style={{ marginTop: 'auto' }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            paddingTop: 12,
            borderTop: `1px solid ${HAIRLINE}`,
          }}>
            <span style={{
              fontFamily: fBody, fontSize: 12, fontWeight: 500,
              color: hovered ? ACCENT : MUTED,
              transition: 'color 0.2s',
            }}>
              Ver detalle
            </span>
            <span style={{
              fontFamily: fBody, fontSize: 14,
              color: hovered ? ACCENT : MUTED,
              transition: 'color 0.2s, transform 0.2s',
              transform: hovered ? 'translateX(2px)' : 'translateX(0)',
            }}>
              →
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};

/* ── Main Catalog ────────────────────────────────────────────── */
export const LaptopAdvisorCatalog = ({
  products = [],
  onViewDetail,
  search = '',
  onSearchChange,
  purpose: purposeProp,
  onPurposeChange,
  flashOnly: flashOnlyProp,
  onFlashOnlyChange,
  faction: factionProp,
  onFactionChange,
  era: eraProp,
  onEraChange,
  collectibleOnly: collectibleOnlyProp,
  onCollectibleOnlyChange,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}) => {
  const { isMobile, isTablet } = useBreakpoint();

  // Filtros controlados (padre pasa setters → estado vive arriba) o
  // no-controlados (landing → estado local interno + filtro cliente).
  const [purpose, setPurpose] = useFilter(purposeProp, onPurposeChange, null);
  const [flashOnly, setFlash] = useFilter(flashOnlyProp, onFlashOnlyChange, false);
  const [faction, setFaction] = useFilter(factionProp, onFactionChange, null);
  const [era, setEra] = useFilter(eraProp, onEraChange, null);
  const [brand, setBrand] = useFilter(undefined, undefined, null);
  const [collectibleOnly, setCollectibleOnly] = useFilter(collectibleOnlyProp, onCollectibleOnlyChange, false);

  const safe = useMemo(() => Array.isArray(products) ? products : [], [products]);

  // Filtro cliente como capa defensiva: garantiza UI consistente aunque el back no
  // esté reiniciado, y es la única fuente de verdad en modo no-controlado (landing).
  const filtered = useMemo(() => safe.filter(p => {
    if (!p || p.is_active === false) return false;
    if (flashOnly && !isFlashOffer(p)) return false;
    if (!matchesPurpose(p, purpose)) return false;
    if (brand && norm(p.brand) !== norm(brand)) return false;
    if (faction && String(p.faction ?? '').toLowerCase() !== faction.toLowerCase()) return false;
    if (era && String(p.era ?? '').toLowerCase() !== era.toLowerCase()) return false;
    if (collectibleOnly && !p.is_collectible) return false;
    return true;
  }), [safe, flashOnly, purpose, brand, faction, era, collectibleOnly]);

  // Counts independientes del filtro activo: pool sin filtros (compartido vía
  // React Query con cualquier otro consumidor que pida lo mismo). Así los chips
  // de Gaming/Office no caen a 0 cuando se selecciona el contrario.
  const { data: countsData } = useProducts({ limit: 200 });
  const countPool = useMemo(
    () => countsData?.pages?.flatMap(p => p.items) ?? [],
    [countsData],
  );
  const counts = useMemo(() => {
    const pool = countPool.length > 0 ? countPool : safe;
    const c = { legendary: pool.filter(isFlashOffer).length };
    c.collectible = pool.filter(p => p.is_collectible).length;
    c.group = {};
    c.leaf = {};
    for (const p of pool) {
      const leaf = getLeaf(p);
      if (!leaf) continue;
      c.leaf[leaf] = (c.leaf[leaf] || 0) + 1;
      const g = LEAF_TO_GROUP[leaf];
      c.group[g] = (c.group[g] || 0) + 1;
    }
    return c;
  }, [countPool, safe]);

  // Marcas del catálogo (sin duplicar por mayúsculas), de mayor a menor.
  const brands = useMemo(() => {
    const pool = countPool.length > 0 ? countPool : safe;
    const m = {};
    for (const p of pool) {
      if (!p?.brand || p.is_active === false) continue;
      const k = norm(p.brand);
      m[k] = m[k] || { label: brandLabel(p.brand), n: 0 };
      m[k].n += 1;
    }
    return Object.values(m).sort((a, b) => b.n - a.n).map(({ label, n }) => [label, n]);
  }, [countPool, safe]);

  // Valores de facción/era derivados del pool (sin endpoint dedicado).
  const { factions, eras } = useMemo(() => {
    const pool = countPool.length > 0 ? countPool : safe;
    const f = {};
    const e = {};
    for (const p of pool) {
      if (p.faction) f[p.faction] = (f[p.faction] || 0) + 1;
      if (p.era) e[p.era] = (e[p.era] || 0) + 1;
    }
    return {
      factions: Object.entries(f).sort((a, b) => b[1] - a[1]),
      eras: Object.entries(e).sort((a, b) => b[1] - a[1]),
    };
  }, [countPool, safe]);

  const selectedQuadrant    = flashOnly ? 'legendary' : null;
  const setSelectedQuadrant = (v) => setFlash(v === 'legendary');
  const purposeFilter       = purpose;
  const setPurposeFilter    = setPurpose;

  return (
    <div style={{
      background: INK, fontFamily: fBody,
      borderTop: `1px solid ${HAIRLINE}`,
    }}>

      {/* ── Header ── */}
      <div style={{
        borderBottom: `1px solid ${HAIRLINE}`,
        padding: 'clamp(40px,6vw,80px) clamp(20px,5vw,80px) clamp(32px,4vw,48px)',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          {/* Eyebrow */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
            <div style={{ width: 28, height: 1, background: 'var(--accent-dim)' }} />
            <Micro>Catálogo · {safe.length} piezas</Micro>
          </div>

          {/* Headline + Search */}
          <div style={{
            display: 'flex',
            flexDirection: isTablet ? 'column' : 'row',
            alignItems: isTablet ? 'stretch' : 'flex-end',
            justifyContent: 'space-between',
            gap: isTablet ? 28 : 48,
          }}>
            <h1 style={{
              fontFamily: fDisplay, fontWeight: 300,
              fontSize: 'clamp(34px, 6vw, 68px)',
              lineHeight: 1.02, letterSpacing: '-0.035em',
              color: CREAM, margin: 0,
            }}>
              Encuentra tu <strong style={{ fontWeight: 600 }}>pieza ideal.</strong>
            </h1>

            <div style={{ flex: '0 0 auto', minWidth: 240, width: isTablet ? '100%' : '35%', maxWidth: 400 }}>
              <label style={{
                fontFamily: fMono, fontSize: 10, fontWeight: 400,
                letterSpacing: '0.16em', textTransform: 'uppercase',
                color: FAINT, display: 'block', marginBottom: 10,
              }}>
                Buscar
              </label>
              <input
                value={search}
                onChange={e => onSearchChange ? onSearchChange(e.target.value) : null}
                placeholder="Marca, modelo, specs…"
                style={{
                  width: '100%', background: 'transparent',
                  border: 'none', borderBottom: `1px solid ${HAIRLINE_STRONG}`,
                  padding: '10px 0', fontFamily: fBody, fontSize: 14,
                  color: CREAM, outline: 'none', boxSizing: 'border-box',
                  transition: 'border-color 0.25s',
                }}
                onFocus={e => e.target.style.borderBottomColor = ACCENT}
                onBlur={e => e.target.style.borderBottomColor = HAIRLINE_STRONG}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{
        maxWidth: 1280, margin: '0 auto',
        padding: 'clamp(32px,5vw,56px) clamp(20px,5vw,80px) clamp(64px,8vw,120px)',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(180px, 220px) 1fr',
        gap: isMobile ? 24 : 'clamp(40px, 5vw, 72px)',
      }}>

        {/* Filters: sidebar desktop, chip bar mobile */}
        {isMobile ? (
          <FilterBar
            counts={counts}
            selectedQuadrant={selectedQuadrant}
            onSelectQuadrant={setSelectedQuadrant}
            purposeFilter={purposeFilter}
            setPurposeFilter={setPurposeFilter}
            brands={brands}
            brand={brand}
            setBrand={setBrand}
            factions={factions}
            faction={faction}
            setFaction={setFaction}
            eras={eras}
            era={era}
            setEra={setEra}
            collectibleOnly={collectibleOnly}
            setCollectibleOnly={setCollectibleOnly}
          />
        ) : (
          <Sidebar
            counts={counts}
            selectedQuadrant={selectedQuadrant}
            onSelectQuadrant={setSelectedQuadrant}
            purposeFilter={purposeFilter}
            setPurposeFilter={setPurposeFilter}
            brands={brands}
            brand={brand}
            setBrand={setBrand}
            factions={factions}
            faction={faction}
            setFaction={setFaction}
            eras={eras}
            era={era}
            setEra={setEra}
            collectibleOnly={collectibleOnly}
            setCollectibleOnly={setCollectibleOnly}
          />
        )}

        <div>
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 16,
            marginBottom: 24, paddingBottom: 16,
            borderBottom: `1px solid ${HAIRLINE}`,
          }}>
            <Micro>
              {String(filtered.length).padStart(2, '0')} / {String(safe.length).padStart(2, '0')} resultados
            </Micro>
            {filtered.length > 0 && !isMobile && (
              <Micro>Click para detalle</Micro>
            )}
          </div>

          {filtered.length === 0 ? (
            <div style={{ padding: '100px 0', textAlign: 'center' }}>
              <Micro style={{ display: 'block', marginBottom: 16 }}>Sin resultados</Micro>
              <p style={{
                fontFamily: fDisplay, fontSize: 18, fontWeight: 300,
                color: MUTED, letterSpacing: '-0.01em',
              }}>
                Ningún equipo coincide con los filtros.
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '160px' : '240px'}, 1fr))`,
              gap: 'clamp(14px, 1.8vw, 24px)',
            }}>
              {filtered.map(p => (
                <ProductCard key={p.id ?? p.id_product} product={p} onViewDetail={onViewDetail} />
              ))}
            </div>
          )}

          {hasNextPage && (
            <div style={{ textAlign: 'center', marginTop: 40 }}>
              <button
                onClick={onLoadMore}
                disabled={isFetchingNextPage}
                style={{
                  padding: '14px 40px',
                  background: 'transparent',
                  border: `1px solid ${HAIRLINE_STRONG}`,
                  color: isFetchingNextPage ? FAINT : MUTED,
                  fontFamily: fMono, fontSize: 10,
                  letterSpacing: '0.16em', textTransform: 'uppercase',
                  cursor: isFetchingNextPage ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { if (!isFetchingNextPage) { e.currentTarget.style.color = CREAM; e.currentTarget.style.borderColor = HAIRLINE_STRONG; } }}
                onMouseLeave={e => { e.currentTarget.style.color = MUTED; e.currentTarget.style.borderColor = HAIRLINE_STRONG; }}
              >
                {isFetchingNextPage ? 'Cargando…' : 'Ver más →'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LaptopAdvisorCatalog;
